import {
    COMMITMENT_DAYS,
    COMMITMENT_FREQUENCY,
    COMMITMENT_SLOTS,
} from '../constants/commitment';
import { sanitizeInput } from './sanitizeInput';

export const COMMITMENT_DAY_KEYS = COMMITMENT_DAYS.map((day) => day.id);
export const COMMITMENT_SLOT_IDS = COMMITMENT_SLOTS.map((slot) => slot.id);
export const COMMITMENT_FREQUENCY_OPTIONS = COMMITMENT_FREQUENCY.map((freq) => freq.id);

export const createEmptyCommitmentSchedule = () => COMMITMENT_DAY_KEYS.map((day) => ({
    day,
    slots: [],
}));

export const normalizeCommitmentDetails = (rawValue) => {
    if (!rawValue) {
        return {
            schedule: createEmptyCommitmentSchedule(),
            frequency: '',
            notes: '',
        };
    }
    try {
        const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
        const schedule = Array.isArray(parsed?.schedule) ? parsed.schedule : [];
        const normalizedSchedule = COMMITMENT_DAY_KEYS.map((day) => {
            const existing = schedule.find((item) => item?.day === day);
            const slots = Array.isArray(existing?.slots)
                ? existing.slots
                    .map((slot) => (COMMITMENT_SLOT_IDS.includes(slot) ? slot : null))
                    .filter(Boolean)
                : [];
            return { day, slots };
        });
        const frequency = COMMITMENT_FREQUENCY_OPTIONS.includes(parsed?.frequency)
            ? parsed.frequency
            : '';
        const notesSource = typeof parsed === 'string'
            ? parsed
            : (parsed?.notes || '');
        const notes = sanitizeInput(notesSource);
        return { schedule: normalizedSchedule, frequency, notes };
    } catch (err) {
        return {
            schedule: createEmptyCommitmentSchedule(),
            frequency: '',
            notes: sanitizeInput(rawValue || ''),
        };
    }
};

export const serializeCommitmentDetails = (details) => {
    const schedule = Array.isArray(details?.schedule) ? details.schedule : createEmptyCommitmentSchedule();
    const normalizedSchedule = COMMITMENT_DAY_KEYS.map((day) => {
        const entry = schedule.find((item) => item?.day === day);
        const slots = Array.isArray(entry?.slots)
            ? entry.slots
                .map((slot) => (COMMITMENT_SLOT_IDS.includes(slot) ? slot : null))
                .filter(Boolean)
            : [];
        return { day, slots };
    });
    const frequency = COMMITMENT_FREQUENCY_OPTIONS.includes(details?.frequency)
        ? details.frequency
        : '';
    const notes = sanitizeInput(details?.notes || '');
    if (!notes && !frequency && normalizedSchedule.every((day) => day.slots.length === 0)) {
        return '';
    }
    return JSON.stringify({
        schedule: normalizedSchedule,
        frequency,
        notes,
    });
};

export const hasCommitmentDetails = (rawValue) => {
    const normalized = normalizeCommitmentDetails(rawValue);
    const hasSchedule = normalized.schedule.some((day) => Array.isArray(day.slots) && day.slots.length > 0);
    const hasFrequency = Boolean(normalized.frequency);
    const hasNotes = Boolean((normalized.notes || '').trim());
    return hasSchedule || hasFrequency || hasNotes;
};

export const formatCommitmentSummary = (rawValue, t) => {
    const translate = typeof t === 'function'
        ? (key, fallback) => t(key, fallback)
        : (_, fallback) => fallback || '';
    const normalized = normalizeCommitmentDetails(rawValue);
    const scheduleSummary = normalized.schedule
        .filter((day) => Array.isArray(day.slots) && day.slots.length > 0)
        .map((day) => {
            const dayLabel = COMMITMENT_DAYS.find((entry) => entry.id === day.day);
            const labelText = translate(dayLabel?.labelKey, day.day);
            const slotText = day.slots
                .map((slotId) => {
                    const slotLabel = COMMITMENT_SLOTS.find((slot) => slot.id === slotId);
                    return translate(slotLabel?.labelKey, slotId);
                })
                .filter(Boolean)
                .join(', ');
            return slotText ? `${labelText}: ${slotText}` : '';
        })
        .filter(Boolean)
        .join('; ');
    const frequencyLabel = normalized.frequency
        ? (COMMITMENT_FREQUENCY.find((freq) => freq.id === normalized.frequency) || {}).labelKey
        : '';
    const frequencyText = frequencyLabel
        ? translate(frequencyLabel, normalized.frequency)
        : '';
    const notesText = normalized.notes || '';
    const parts = [];
    if (scheduleSummary) {
        parts.push(`${translate('home.profileSections.commitment.scheduleTitle', 'Schedule')}: ${scheduleSummary}`);
    }
    if (frequencyText) {
        parts.push(`${translate('home.profileSections.commitment.frequencyTitle', 'Rehearsal cadence')}: ${frequencyText}`);
    }
    if (notesText) {
        parts.push(`${translate('home.profileSections.commitment.notesTitle', 'Additional notes')}: ${notesText}`);
    }
    return parts.join(' · ');
};
